import React from 'react'
import styled, { css } from 'styled-components'
import { graphql, useStaticQuery, navigate } from 'gatsby'

// Images
import check from 'img/check.svg'

// Components
import Plaatjie from '@ubo/plaatjie'
import ButtonPrimaryTransparent from 'components/elements/Buttons/ButtonPrimaryTransparent'

// Elements
import ParseContent from 'components/shared/ParseContent'
import BlogBink, {
  useBlogBink,
  BlogBinkFilter,
  BlogBinkPosts,
} from '@ubo/blog-bink'

interface BlogProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_PostTypes
}

const Title = styled(ParseContent)`
  & h1,
  h2 {
    font-weight: ${({ theme }) => theme.font.weight.bold};
    font-size: 50px;
    font-family: ${({ theme }) => theme.font.family.secondary};
    position: relative;
    padding-left: 50px;
    padding-bottom: 0;
    margin-bottom: 40px !important;

    &:before {
      content: '';
      position: absolute;
      height: 100%;
      width: 3px;
      background-color: ${({ theme }) => theme.color.primary};
      left: 0px;
    }
  }

  @media screen and (max-width: 1199px) {
    & h1,
    h2 {
      padding-left: 20px;
    }
  }

  @media screen and (max-width: 575px) {
    & h1,
    h2 {
      margin-bottom: 20px !important;
      font-size: 30px;
      line-height: 40px;
    }
  }
`

const FilterContainer = styled.div`
  background-color: ${({ theme }) => theme.color.grey};
  height: 100%;
  position: relative;

  &:before {
    content: '';
    background-color: ${({ theme }) => theme.color.grey};
    height: 100%;
    width: 2000px;
    left: -2000px;
    top: 0;
    position: absolute;
  }

  @media screen and (max-width: 991px) {
    &:after {
      content: '';
    }
  }
`

const FilterTitle = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.bold};
  font-size: 20px;
  line-height: 30px;
  color: ${({ theme }) => theme.color.primary};
`

const News: React.FC<BlogProps> = ({ fields }) => {
  const {
    allWpPost,
    allWpCategory,
    // eslint-disable-next-line
  } = useStaticQuery<GatsbyTypes.blogQueryQuery>(graphql`
    query blogQuery {
      allWpPost(sort: { order: DESC, fields: date }) {
        edges {
          node {
            ...generalPostFragment
          }
        }
      }
      allWpCategory(filter: { name: { nin: "Uncategorized" } }) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  `)

  const posts: unknown = allWpPost.edges

  return (
    <section>
      <BlogBink
        posts={posts as BlogBinkPosts}
        categories={allWpCategory.edges}
        id="blog"
        limit={Number(fields.limit)}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              <FilterContainer className="py-xl-5 py-lg-4 py-3">
                <Title content={fields.title || ''} />
                <FilterTitle>Filter by category</FilterTitle>
                <BlogFilters />
              </FilterContainer>
            </div>
            <div className="col-lg-9 py-lg-5 py-4">
              <BlogGrid fields={fields} />
            </div>
          </div>
        </div>
      </BlogBink>
    </section>
  )
}

const StyledBlogFilter = styled(BlogBinkFilter)<{ selected: boolean }>`
  padding-left: 25px;
  font-size: 16px;
  line-height: 30px;
  font-weight: ${({ theme }) => theme.font.weight.light};
  position: relative;

  &:before {
    content: '';
    position: absolute;
    height: 14px;
    width: 14px;
    border: 1px solid #dadada;
    top: 0;
    bottom: 0;
    margin-top: auto;
    margin-bottom: auto;
    left: 0px;
  }

  ${({ selected }) =>
    selected &&
    css`
      &:after {
        content: '';
        background-image: url(${check});
        background-size: contain;
        background-repeat: no-repeat;
        height: 12px;
        width: 12px;
        position: absolute;
        top: 1px;
        bottom: 0;
        margin-top: auto;
        margin-bottom: auto;
        left: 1px;
      }
    `}
`

interface BlogFiltersProps {}

interface BlogFilterProps {
  node: {
    id: string
    name: string
  }
}

const BlogFilters: React.FC<BlogFiltersProps> = () => {
  const blogBink = useBlogBink()

  return (
    <div className="pt-lg-2">
      {blogBink.categories.map((category: BlogFilterProps) => (
        <div>
          <StyledBlogFilter
            key={category.node.id}
            id={category.node.id}
            selected={blogBink.isSelected(category.node.id)}
            className="mr-3"
          >
            {category.node.name}
          </StyledBlogFilter>
        </div>
      ))}
    </div>
  )
}

interface BlogGridProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_PostTypes
}

const Col = styled.div`
  @media screen and (max-width: 575px) {
    padding-bottom: 30px;

    &:last-of-type {
      padding-bottom: 0;
    }
  }
`

const BlogGrid: React.FC<BlogGridProps> = ({ fields }) => {
  const blogBink = useBlogBink()

  return (
    <div className="row">
      {blogBink.posts.map((post) => {
        const { node }: any = post

        return (
          <Col key={post.node.id} className="col-md-6">
            <BlogGridPost node={node} blogFields={fields} />
          </Col>
        )
      })}
    </div>
  )
}

interface BlogGridPostProps {
  // eslint-disable-next-line
  blogFields: GatsbyTypes.WpPage_Flexcontent_Flex_PostTypes
  node: GatsbyTypes.WpPost
}

const BlogGridPostExcerpt = styled(ParseContent)`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 120px;
  max-width: 100%;

  & p {
    font-size: 14px;
    line-height: 30px;
    font-weight: ${({ theme }) => theme.font.weight.light};
  }
`

const BlogTitle = styled.h2`
  font-weight: ${({ theme }) => theme.font.weight.bold};
  font-size: 18px;
  line-height: 30px;
  color: ${({ theme }) => theme.color.dark};
`

const StyledBlogGridPost = styled.div`
  padding: 0 0 0 50px;
  margin: 0 0 20px 0;

  @media screen and (max-width: 991px) {
    padding: 0;
    margin: 0 0 10px 0;
  }
`

const StyledPlaatjie = styled(Plaatjie)`
  cursor: pointer;
  height: 250px;
`

const BlogDate = styled.div`
  color: #bcbcbc;
  font-size: 14px;
  line-height: 25px;
  font-weight: ${({ theme }) => theme.font.weight.light};
`

const BlogGridPost: React.FC<BlogGridPostProps> = ({ node, blogFields }) => {
  const currentRawDate = new Date(node.date || '').toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  const currentDate =
    currentRawDate.charAt(0).toUpperCase() + currentRawDate.slice(1)

  return (
    <StyledBlogGridPost>
      <StyledPlaatjie
        onClick={() => navigate(node.uri || '/')}
        image={node.recap?.image}
        alt=""
      />
      <BlogTitle className="pt-lg-4 pt-3">{node.title}</BlogTitle>
      <BlogDate className="pb-lg-2 pb-1">{currentDate}</BlogDate>
      <BlogGridPostExcerpt content={node.recap?.excerpt || ''} />
      <div className="mt-3 d-flex justify-content-end">
        <ButtonPrimaryTransparent to={node.uri || '/'}>
          {blogFields.readmoretext}
        </ButtonPrimaryTransparent>
      </div>
    </StyledBlogGridPost>
  )
}

export default News
